import * as Sentry from '@sentry/react';
import {
    ManualUploadSessionRecording,
    RecorderSessionRecording,
    SessionRecording,
} from './sessionRecordingsDatabase.types';
import { InputFileSource } from '@upheal/shared/types';
import { isAvailableSessionType } from '../../state/isAvailableSessionType';
import { GQLSessionNoteType } from '@upheal/graphql/definitions/schema.generated';
import { browserStorage } from '../cache/browserStorage';
import { captureException } from '../sentry';
import throttle from 'lodash.throttle';
import logger from '@upheal/utils/logger';
import posthog from 'posthog-js';
import { isDevelopEnv } from '../configuration';

export const LOG_ACTIVITY_INTERVAL = 5_000; // 5 seconds
const ACTIVITY_THRESHOLD = 1000 * 60 * 10; // 10 minutes
export const MINIMAL_RECORDING_DURATION = isDevelopEnv ? 15_000 : 60_000; // 1 minute

export const S3_RETENTION_PERIOD = 1000 * 60 * 60 * 24 * 3.5; // 3.5 days, s3 is configured to delete files older than 4 days

// Let TS check that we have all inputFileSources covered
const RECORDER_INPUT_FILE_SOURCES_OBJECT: Record<
    RecorderSessionRecording['inputFileSource'],
    RecorderSessionRecording['inputFileSource']
> = {
    [InputFileSource.AudioRecorder]: InputFileSource.AudioRecorder,
    [InputFileSource.ExtensionRecorder]: InputFileSource.ExtensionRecorder,
    [InputFileSource.VirtualSessionRecorder]:
        InputFileSource.VirtualSessionRecorder,
};

export const RECORDER_INPUT_FILE_SOURCES = Object.values(
    RECORDER_INPUT_FILE_SOURCES_OBJECT,
);

export const isRecorderSessionRecording = (
    recording: SessionRecording,
): recording is RecorderSessionRecording => {
    return RECORDER_INPUT_FILE_SOURCES.includes(recording.inputFileSource);
};

export const isManualUploadSessionRecording = (
    recording: SessionRecording,
): recording is ManualUploadSessionRecording => {
    return recording.inputFileSource === InputFileSource.ManualUpload;
};

export const getVisibilityState = () => {
    if (typeof document === 'undefined') {
        return;
    }

    return document.visibilityState;
};

export const isRecordingUploadedInS3 = (recording: SessionRecording) => {
    return (
        recording.isUploaded &&
        recording.uploadUrl &&
        recording.uploadedAt &&
        recording.uploadedAt > new Date(Date.now() - S3_RETENTION_PERIOD)
    );
};

export const isRecordingInProgress = (recording: SessionRecording) => {
    return (
        !recording.isEnded &&
        !recording.isUploading &&
        !recording.isUploaded &&
        !recording.isCreating &&
        recording.lastActivity >
            new Date(Date.now() - LOG_ACTIVITY_INTERVAL * 1.1)
    );
};

export const canContinueWithRecording = (recording: SessionRecording) => {
    return (
        !recording.isEnded &&
        !recording.isUploading &&
        !recording.isUploaded &&
        !recording.isCreating &&
        recording.lastActivity > new Date(Date.now() - ACTIVITY_THRESHOLD)
    );
};

export const isUploadInProgress = (recording: SessionRecording) => {
    return (
        (recording.isUploading ||
            recording.isUploaded ||
            recording.isCreating) &&
        recording.lastUploadOrCreateAttempt &&
        recording.lastUploadOrCreateAttempt >
            new Date(Date.now() - LOG_ACTIVITY_INTERVAL * 1.5)
    );
};

export const isRecordingEligibleForBackgroundUpload = (
    recording: SessionRecording,
    recordingFilesCount: number,
    availableSessionTypes?: GQLSessionNoteType[],
) => {
    if (recording.inputFileSource === InputFileSource.ManualUpload) {
        return;
    }

    if (
        !recording.finishedSessionEndDialog ||
        !recording.sessionType ||
        !isAvailableSessionType(recording.sessionType, availableSessionTypes)
    ) {
        return false;
    }

    if (
        !recording.sessionStart ||
        recordingFilesCount === 0 ||
        recording.unpausedSessionDuration < MINIMAL_RECORDING_DURATION
    ) {
        return false;
    }

    if (recording.isUploading || recording.isUploaded || recording.isCreating) {
        return !isUploadInProgress(recording);
    }

    if (recording.isEnded) {
        // Do not upload yet if the recording was still active very recently, to avoid duplicate uploads caused by various race-conditions and edge-cases
        return recording.lastActivity < new Date(Date.now() - 1000 * 60 * 1); // 1 minute;
    }

    return !canContinueWithRecording(recording);
};

const markError = (error: unknown) => {
    if (error instanceof Error && error.name === 'DatabaseClosedError') {
        browserStorage.incrementSessionRecordingsDatabaseErrorsCount();
    }
};

export function LogError({
    errorName,
    level = 'error',
}: {
    errorName?: string;
    level?: Sentry.SeverityLevel;
} = {}) {
    return (
        _target: any,
        methodName: string | symbol,
        descriptor: PropertyDescriptor,
    ) => {
        const originalMethod = descriptor.value;
        const resolvedErrorName =
            errorName ?? `[srd] Error when executing ${methodName.toString()}`;

        // Throttle the Sentry logging (max 1 log every 5 minutes per method) to avoid spamming the logs and eating up the quota
        // Complete logs are sent to BetterStacks
        const throttledSentryLog = throttle(
            (error: unknown, args: Array<unknown>) => {
                captureException(
                    resolvedErrorName,
                    {
                        error,
                        args,
                    },
                    {
                        level,
                    },
                );
            },
            5 * 60_000,
            {
                leading: true,
                trailing: true,
            },
        );

        descriptor.value = async function (...args: any[]) {
            try {
                const shouldLogName = posthog.isFeatureEnabled(
                    'track-srd-db-operation-duration',
                );

                shouldLogName &&
                    logger.debug(
                        `[srd] Executing method ${methodName.toString()}`,
                    );
                return await originalMethod.apply(this, args);
            } catch (error) {
                throttledSentryLog(error, args);
                // eslint-disable-next-line no-restricted-syntax
                logger.error(resolvedErrorName, { error, args });

                markError(error);

                throw error; // rethrow after logging
            }
        };

        return descriptor;
    };
}
